import { useCallback, useEffect, useState } from 'react';

export type Appearance = 'light' | 'dark' | 'system';

const prefersDark = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const setCookie = (name: string, value: string, days = 365) => {
    if (typeof document === 'undefined') {
        return;
    }

    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const applyTheme = (appearance: Appearance) => {
    const isDark = appearance === 'dark' || (appearance === 'system' && prefersDark());

    document.documentElement.classList.toggle('dark', isDark);
};

const mediaQuery = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
};

const handleSystemThemeChange = () => {
    const currentAppearance = localStorage.getItem('appearance') as Appearance;
    applyTheme(currentAppearance || 'system');
};

export function initializeTheme() {
    const savedAppearance = (localStorage.getItem('appearance') as Appearance) || 'system';

    applyTheme(savedAppearance);

    // Add the event listener for system theme changes...
    mediaQuery()?.addEventListener('change', handleSystemThemeChange);
}

export function useAppearance() {
    // Initialize from localStorage immediately to prevent flash
    const getInitialAppearance = (): Appearance => {
        if (typeof window === 'undefined') return 'system';
        return (localStorage.getItem('appearance') as Appearance) || 'system';
    };

    const [appearance, setAppearance] = useState<Appearance>(getInitialAppearance);

    const updateAppearance = useCallback((mode: Appearance) => {
        setAppearance(mode);

        // Store in localStorage for client-side persistence...
        localStorage.setItem('appearance', mode);

        // Store in cookie for SSR...
        setCookie('appearance', mode);

        applyTheme(mode);
    }, []);

    useEffect(() => {
        // Ensure theme is applied on mount
        const savedAppearance = getInitialAppearance();
        applyTheme(savedAppearance);

        // Listen for system theme changes
        const mediaQueryInstance = mediaQuery();
        mediaQueryInstance?.addEventListener('change', handleSystemThemeChange);

        return () => {
            mediaQueryInstance?.removeEventListener('change', handleSystemThemeChange);
        };
    }, []);

    return { appearance, updateAppearance } as const;
}
